import { useEffect, useCallback } from 'react';
import { Platform, AppState, AppStateStatus } from 'react-native';
import { trpc } from '@/trpc/react';

// Check if running in Expo Go and load expo-notifications
let Constants: any;
let ExpoNotifications: any;
let isRunningInExpoGo = false;

if (Platform.OS !== 'web') {
    try {
        Constants = require('expo-constants').default;
        isRunningInExpoGo = Constants.appOwnership === 'expo';

        // Only try to load expo-notifications if not in Expo Go
        // The app has native notification support through OneSignal, so expo-notifications should work
        if (!isRunningInExpoGo) {
            try {
                ExpoNotifications = require('expo-notifications');
            } catch {
                // Expo notifications not available
            }
        }
    } catch {
        // Constants not available
    }
}

/**
 * Hook to manage app badge count based on unread messages
 * Updates the app icon badge to show unread message count
 * 
 * Note: Badge updates require native notification support. This hook will
 * attempt to set badges using available APIs, but full badge support requires
 * the app to be built with notification permissions.
 */
export const useBadgeCount = (crewId: number | undefined) => {
    // Query to get unread message count
    const { data: unreadCount = 0, refetch } = trpc.messages.getUnreadMessageCount.useQuery(
        { crew_id: crewId! },
        {
            enabled: !!crewId && Platform.OS !== 'web',
            refetchInterval: 30000, // Check every 30 seconds

        }
    );

    // Function to update the badge count
    const updateBadgeCount = useCallback((count: number) => {
        if (Platform.OS === 'web') return;

        // Don't try to update badge in Expo Go

        try {
            // Use expo-notifications to set badge count
            // This works because the app has native notification support through OneSignal
            if (ExpoNotifications && ExpoNotifications.setBadgeCountAsync) {
                ExpoNotifications.setBadgeCountAsync(count).catch((error: any) => {
                    console.warn('Failed to set badge count:', error);
                });
                console.log(`Badge count set to ${count}`);
            } else {
                console.log(`Expo notifications not available: unread count is ${count}`);
            }
        } catch (error) {
            console.error('Error updating badge count:', error);
        }
    }, []);

    // Update badge when unread count changes
    useEffect(() => {
        if (unreadCount !== undefined) {
            updateBadgeCount(unreadCount);
        }
    }, [unreadCount, updateBadgeCount]);

    // Refresh when app comes to foreground
    useEffect(() => {
        const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
            if (nextAppState === 'active' && crewId) {
                refetch();
            }
        });

        return () => {
            subscription.remove();
        };
    }, [crewId, refetch]);

    return {
        unreadCount,
        refreshBadgeCount: refetch,
    };
};

